import {
  createComponentMapperModel,
  withCompInfo,
} from '@wix/editor-elements-integrations';
import type {
  RatingsInputDefinition,
  IRatingsInputMapperProps,
  RatingsInputCSSVars,
  RatingsInputCssCarmiData,
  RatingsInputPropsCarmiData,
  RatingsInputProperties,
  RatingsInputLabelPosition,
  RatingsInputStyleProperties,
} from '../RatingsInput.types';
import {
  castFromInheritIfNeeded,
  castToInheritIfNeeded,
  convertPhysicalAlignmentToLogical,
  withFallbackValues,
  withSiteDirection,
} from '@wix/editor-elements-common-utils';

export const props = withCompInfo<
  IRatingsInputMapperProps,
  RatingsInputDefinition,
  RatingsInputPropsCarmiData
>()(
  ['compData', 'compProps', 'isMobileView'],
  ({ compData, compProps, isMobileView }, carmiProps) => {
    const showA11yLabel =
      !isMobileView && !!compData.titleText && !!compProps.a11yLabelPosition;

    return {
      ...carmiProps,
      labelPosition:
        isMobileView && compProps.labelPosition === 'side'
          ? 'top'
          : compProps.labelPosition,
      showTitle: compProps.showTitle && !showA11yLabel,
      title: compData.titleText,
      showLabels: compProps.showLabels,
      value: compData.value,
      labels: compData.labels,
      isDisabled: compProps.isDisabled,
      required: compProps.required,
      a11yLabelPosition: compProps.a11yLabelPosition,
      shapeSize: compProps.shapeSize,
      showA11yLabel,
    };
  },
);

const getIconsMarginCss = (
  compProps: RatingsInputProperties,
  labelPosition: RatingsInputLabelPosition,
) => {
  const { shapeSize, shapeSpacing } = compProps;
  const margin = `${
    labelPosition === 'side' ? Math.max(15, shapeSpacing) : shapeSize / 2
  }px`;

  if (labelPosition === 'top') {
    return { '--iconsMarginTop': margin };
  }
  if (labelPosition === 'bottom') {
    return { '--iconsMarginBottom': margin };
  }
  return { '--iconsMarginEnd': margin };
};

const getA11yCss = (
  {
    a11yLabelPosition,
    labelPosition,
    labelMargin = 0,
    labelPadding = 0,
  }: RatingsInputProperties,
  { a11yLabelAlign, align }: RatingsInputStyleProperties,
) => {
  const css: Partial<RatingsInputCSSVars> = {
    '--a11yFlexDirection': a11yLabelPosition === 'side' ? 'row' : 'column',
    '--a11yLabelAlign': a11yLabelAlign,
  };

  const calculatedA11LabelAlign = castFromInheritIfNeeded(
    a11yLabelAlign,
    align,
  );

  if (a11yLabelPosition === 'top') {
    return {
      ...css,
      '--a11yLabelMargin': `0 0 ${labelMargin}px 0`,
      ...(calculatedA11LabelAlign === 'start' && {
        '--a11yLabelPaddingStart': `${labelPadding}px`,
        '--a11yLabelPaddingEnd': '0',
      }),
      ...(calculatedA11LabelAlign === 'end' && {
        '--a11yLabelPaddingStart': '0',
        '--a11yLabelPaddingEnd': `${labelPadding}px`,
      }),
    };
  }

  if (a11yLabelPosition === 'side') {
    return {
      ...css,
      '--a11yLabelAlign': 'start',
      '--inputAlignItems': labelPosition === 'top' ? 'flex-end' : 'flex-start',
    };
  }

  return css;
};

const labelsContainerAlignment = {
  top: 'flex-end',
  bottom: 'flex-start',
  side: 'center',
};

export const css = withCompInfo<
  RatingsInputCSSVars,
  RatingsInputDefinition,
  RatingsInputCssCarmiData,
  { styleProperties: RatingsInputStyleProperties }
>()(
  ['compProps', 'compData', 'styleProperties', 'isMobileView'],
  ({ compProps, compData, styleProperties, isMobileView }, carmiCss) => {
    const { showTitle, showLabels, shapeSize, shapeSpacing } = compProps;
    const { align, labelAlign = 'inherit' } = styleProperties;
    const { titleText, direction = 'inherit' } = compData;

    const a11y = !isMobileView && !!titleText;
    const iconSize = `${shapeSize * 5 + shapeSpacing * 4}px`;
    const labelPosition =
      isMobileView && compProps.labelPosition === 'side'
        ? 'top'
        : compProps.labelPosition;

    const shouldAddIconsMargin =
      (showTitle && !!titleText && !a11y) || showLabels;

    return {
      ...carmiCss,
      '--iconsMinWidth': iconSize,
      '--iconsOrder': labelPosition === 'top' ? 2 : 0,
      '--labelsContainerAlignItems': labelsContainerAlignment[labelPosition],
      '--labelPosition': labelPosition === 'side' ? 'absolute' : 'static',
      '--inputTextAlign': `${labelAlign}`,
      '--iconSize': `${shapeSize}px`,
      '--iconPaddingEnd': `${shapeSpacing}px`,
      '--direction': withSiteDirection(direction),
      '--align': align,
      height: 'auto',
      ...(labelPosition === 'side'
        ? {
            '--inputAlignItems': 'center',
            '--inputFlexDirection': 'row',
            'min-width': 'min-content',
          }
        : {
            '--inputAlignItems': align,
            '--labelsContainerWidth': iconSize,
            '--inputFlexDirection': 'column',
          }),
      ...(shouldAddIconsMargin && getIconsMarginCss(compProps, labelPosition)),
      ...(a11y && getA11yCss(compProps, styleProperties)),
    };
  },
  [
    withFallbackValues([
      {
        sourceNamespace: 'compProps',
        targetNamespace: 'compData',
        fields: ['direction'],
        enhancer: direction => castToInheritIfNeeded(direction, 'ltr'),
      },
      {
        sourceNamespace: 'compProps',
        targetNamespace: 'styleProperties',
        fields: [
          { source: 'labelAlignment', target: 'align' },
          { source: 'a11yLabelAlignment', target: 'a11yLabelAlign' },
        ],
        enhancer: (alignment, { compProps }) =>
          convertPhysicalAlignmentToLogical(alignment, compProps.direction),
      },
    ]),
  ],
);

export default createComponentMapperModel({ props, css });
